import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { array, InferType, object } from 'yup';
import dayjs from 'dayjs';

import {
  PriceReadModel,
  SetAgreementProductPayload,
  TenantErpIntegrationReadModel,
  UnitScheme,
  useGetAgreementsByIdQuery,
  useGetPricesQuery,
  usePutAgreementsByAgreementIdProductsMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  FormSubmitButton,
  isDefinedString,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  useFormAdd,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { AgreementProductItem } from './agreement-product-item.component';
import {
  AgreementProductFormData,
  AgreementProductItemForm,
  agreementProductSchema,
} from './agreement-product-item.form';

import { CancelRequestModal, ProductPricesAddModal } from '../../../../../components';
import { AgreementPermissionBasedActionButton } from '../../../../../components/agreement-permission-based-button/agreement-permission-based-button';
import { useAgreementPermissions } from '../../../../../hooks/useAgreementPermissions/useAgreementPermissions';
import { useTenants } from '../../../../../hooks';
import { PermissionKeys } from '@client/api';

const schema = object({
  products: array().of(agreementProductSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export type AgreementProductsFormData = FormValues;

export interface AgreementProductsCardProps {
  className?: string;
  title?: string;
  unitSchemes?: UnitScheme[];
}

export function AgreementProductsCard({ className, unitSchemes, ...props }: AgreementProductsCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tAgreements } = useTranslation('agreements');

  const { title = tGlobal('entity.agreementProduct', { context: 'plural' }) } = props;

  const { id } = useParams<{ id: string }>();
  const { selectedTenant } = useTenants();

  // Check if ERP is configured for the current tenant
  const isErpConfigured = selectedTenant && typeof selectedTenant === 'object' && 'erpIntegrations' in selectedTenant
    ? !!selectedTenant.erpIntegrations?.some((erp: TenantErpIntegrationReadModel) => erp.configured)
    : false;

  // const pageSize = 20;
  // const [page, setPage] = useState(0);

  const [editing, setEditing] = useState<boolean>(false);

  const { data: agreement, isFetching, isLoading } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });
  const { hasPermission } = useAgreementPermissions({ id });
  const canEdit = hasPermission([PermissionKeys.Agreement.Update]);

  const isAgreementActive = agreement?.status === 'Effective';
  const isTerminated = agreement?.status === 'Terminated';

  const { data: pricesData, isFetching: isFetchingPrices } = useGetPricesQuery({ 'pagination.Limit': 250 }, { skip: !canEdit });
  const allPrices = useMemo(() => pricesData?.items ?? [], [pricesData?.items]);

  const [putAgreementProducts] = usePutAgreementsByAgreementIdProductsMutation();

  const isFelleskost = (x: { price?: { product?: { category?: string; unit?: { scheme?: string } | null } | null } | null }) =>
    x.price?.product?.category === 'Felleskostnader' ||
    x.price?.product?.unit?.scheme === 'AreaMetric';

  const agreementProducts = useMemo(() => {
    // Exclude Felleskostnader/AreaMetric products (those go to Felleskostnader card) and sub-products
    const products = agreement?.products?.filter((x) =>
      !isFelleskost(x) && !x.parentAgreementProductId
    ) ?? [];

    // If unitSchemes is specified, further filter by those schemes
    if (Array.isArray(unitSchemes)) {
      return products.filter((x) =>
        x.price?.product?.unit?.scheme != null ? unitSchemes.includes(x.price?.product?.unit?.scheme) : false,
      );
    }

    return products;
  }, [agreement?.products, unitSchemes]);

  const otherAgreementProducts = useMemo(() => {
    return agreement?.products?.filter((x) => agreementProducts?.findIndex((y) => x.id === y.id) === -1) ?? [];
  }, [agreement?.products, agreementProducts]);

  // const totalCount = agreementProducts != null && agreementProducts.length > 0 ? agreementProducts.length : 0;

  const onSubmit = async (data: FormValues) => {
    if (id == null) return;

    // Helper to clean interval - return null if empty
    const cleanInterval = (interval: any) => {
      if (!interval) return null;
      if (!interval.start && !interval.end) return null;
      return interval;
    };

    // Helper to clean overwrittenUnitAmount - return null if empty
    const cleanOverwrittenUnitAmount = (amount: any) => {
      if (!amount) return null;
      if (amount.amount == null || amount.amount === '') return null;
      return amount;
    };

    const existingPayload: SetAgreementProductPayload[] =
      otherAgreementProducts.map<SetAgreementProductPayload>(
        (x): SetAgreementProductPayload => ({
          priceId: x.price?.id ?? '',
          quantity: x.quantity as number,
          id: x.id,
          interval: cleanInterval(x.interval),
          overwrittenUnitAmount: cleanOverwrittenUnitAmount(x.overwrittenUnitAmount),
          overrideArea: x.overrideArea ?? null,
        }),
      ) ?? [];

    // Clean the form data products
    const cleanedProducts = data.products.map((p) => ({
      ...p,
      interval: cleanInterval(p.interval),
      overwrittenUnitAmount: cleanOverwrittenUnitAmount(p.overwrittenUnitAmount),
    }));

    try {
      await putAgreementProducts({
        agreementId: id,
        body: [...existingPayload, ...cleanedProducts],
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      products: agreementProducts.map<AgreementProductFormData>((data) => ({
        id: data.id ?? undefined,
        priceId: data.price?.id ?? '',
        quantity: data.quantity ?? ('' as any),
        interval: data.interval ?? null,
        overwrittenUnitAmount: data.overwrittenUnitAmount ?? null,
      })),
    }),
    [agreementProducts],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
  } = methods;

  /**
   * #########################################
   * Edit Agreement Products
   */

  const {
    fields: editedAgreementProducts,
    items: productPrices,
    setItems: setProductPrices,
    adding: addingProductPrices,
    enableAdding: handleOpenAddProductPrices,
    disableAdding: handleCloseAddProductPrices,
    appendNewFormItems,
    removeItemByFieldIndex: handleRemoveProductPrice,
    getItemByFieldIndex: getProductPriceByFieldIndex,
  } = useFormAdd<AgreementProductFormData, PriceReadModel>({
    control,
    name: 'products',
    mapItemToFormItem: (x): AgreementProductFormData => ({
      id: undefined,
      priceId: x.id,
      quantity: 1,
      interval: {
        start: agreement?.duration?.start ?? null,
        end: agreement?.duration?.end ?? null,
      },
      overwrittenUnitAmount: null,
    }),
    itemEqualsFormItem: (a, x) => a.id === x.priceId,
    formItemEqualsFormItem: () => false,
  });

  // Custom add handler: only append NEW items so existing fields preserve
  // their edited values (quantity, overwrittenUnitAmount, interval, etc.)
  const handleAddProductPrices = (newPrices: PriceReadModel[]) => {
    const existingCount = productPrices.length;
    const addedPrices = newPrices.slice(existingCount);
    if (addedPrices.length > 0) {
      appendNewFormItems(
        addedPrices.map((x) => ({
          id: undefined,
          priceId: x.id,
          quantity: 1,
          interval: {
            start: agreement?.duration?.start ?? null,
            end: agreement?.duration?.end ?? null,
          },
          overwrittenUnitAmount: null,
        })),
      );
    }
    setProductPrices(newPrices);
    handleCloseAddProductPrices();
  };

  useEffect(() => {
    setProductPrices(agreementProducts.map((x) => x.price).filter((x): x is PriceReadModel => x != null));
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * #########################################
   */

  const hasAgreementProducts = agreementProducts != null && agreementProducts.length <= 0;
  const hasEditedAgreementProducts = editedAgreementProducts != null && editedAgreementProducts.length <= 0;

  const isListEmpty = useMemo(
    () => (!editing ? hasAgreementProducts : hasEditedAgreementProducts),
    [editing, hasAgreementProducts, hasEditedAgreementProducts],
  );

  const handleOpenEdit = () => {
    setEditing(true);
    // setPage(0);
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  // Calculate total amount locally from filtered agreementProducts (not from backend totals which include all products)
  const totalAmount = useMemo(() => {
    if (!agreementProducts || agreementProducts.length === 0) return undefined;
    
    let yearlyTotal = 0;
    let periodTotal = 0;
    let periodMonths = 0;
    let oneTimeTotal = 0;
    
    for (const product of agreementProducts) {
      const effectiveUnitAmount = product.overwrittenUnitAmount ?? product.price?.unitAmount;
      if (!effectiveUnitAmount || !product.quantity) continue;
      
      const monthlyTotal = effectiveUnitAmount.amount * product.quantity;
      
      if (product.price?.recurring) {
        // Check if product has a custom period less than 12 months
        const intervalStart = product.interval?.start;
        const intervalEnd = product.interval?.end;
        let monthsInPeriod: number | null = null;
        
        if (intervalStart && intervalEnd) {
          const start = dayjs(intervalStart);
          const end = dayjs(intervalEnd).add(1, 'day'); // Add 1 day for inclusive end date
          if (start.isValid() && end.isValid()) {
            monthsInPeriod = Math.max(0, end.diff(start, 'month', true));
          }
        }
        
        if (monthsInPeriod != null && monthsInPeriod < 12) {
          // Product with custom period - add to period total
          periodTotal += monthlyTotal * monthsInPeriod;
          periodMonths = Math.max(periodMonths, Math.round(monthsInPeriod));
        } else {
          // Full year product
          yearlyTotal += monthlyTotal * 12;
        }
      } else {
        // One-time amount
        oneTimeTotal += monthlyTotal;
      }
    }
    
    const formatNumber = (num: number) => new Intl.NumberFormat('nb-NO', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
    
    const parts: string[] = [];
    if (oneTimeTotal !== 0) {
      parts.push(`${formatNumber(oneTimeTotal)} kr Engangs`);
    }
    if (periodTotal !== 0) {
      parts.push(`${formatNumber(periodTotal)} kr (${periodMonths} mnd)`);
    }
    if (yearlyTotal !== 0) {
      parts.push(`${formatNumber(yearlyTotal)} kr / år`);
    }
    
    return parts.length > 0 ? parts.join(' + ') : undefined;
  }, [agreementProducts]);

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="highlighted">
                {title}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <AgreementPermissionBasedActionButton
                    agreementId={id ?? ''}
                    permissions={[PermissionKeys.Agreement.Update]}
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || agreement == null || isTerminated}
                    disabledTooltip={isTerminated ? tAgreements('terminatedAgreementEditDisabled') : undefined}
                    title={tCommon('edit')}
                  />
                ) : (
                  <>
                    <Button color="primary-dark" size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </Button>
                    <Button size="sm" color="primary-dark" disabled={isFetching} onClick={handleOpenAddProductPrices}>
                      {tCommon('add')}
                    </Button>
                    <FormSubmitButton type="submit" color="primary" size="sm" disabled={isFetching}>
                      {tCommon('save')}
                    </FormSubmitButton>
                  </>
                )}
              </CardActions>
            </CardHeader>

            <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
              <Table className="overflow-hidden w-full mb-4 rounded-md " size="xs" layout="fixed">
                {!isLoading && isListEmpty && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {tCommon('noEntitySpecified', {
                        entity: tGlobal('entity.agreementProduct', { context: 'plural' }).toLowerCase(),
                      })}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-[14%]">
                      {tGlobal('entity.agreementProduct', { context: 'short' })}
                    </TableHeadCell>
                    <TableHeadCell className="w-[20%]">{tGlobal('agreementProduct.quantity')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementProduct.start')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementProduct.stop')}</TableHeadCell>
                    <TableHeadCell className="w-[13%]">{tGlobal('agreementProduct.productPrice')}</TableHeadCell>
                    <TableHeadCell className="w-[10%]">{tGlobal('agreementProduct.overwrittenPrice')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementProduct.period')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementProduct.isInvoiced')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]" align="right">{tGlobal('agreementProduct.totalAmount')}</TableHeadCell>
                  </TableHeadRow>
                </TableHead>

                <TableBody>
                  {isLoading &&
                    Array.from(Array(3).keys()).map((x, idx) => <AgreementProductItem key={x} isFetching />)}

                  {!isLoading &&
                    !editing &&
                    agreementProducts?.map((space, index) => (
                      <AgreementProductItem key={space.id} agreementProduct={space} isFetching={isFetching} />
                    ))}

                  {!isLoading &&
                    editing &&
                    editedAgreementProducts?.map((field, index) => {
                      const currentProduct = agreementProducts.find(p => p.id === methods.getValues(`products.${index}.id`));
                      const isReadOnly = isErpConfigured && currentProduct?.isInvoiced;
                      const canRemove = !isReadOnly;
                      
                      return (
                        <AgreementProductItemForm
                          key={field.id}
                          fieldIndex={index}
                          productPrice={getProductPriceByFieldIndex(index)}
                          allPrices={allPrices}
                          isFetching={isFetching || isFetchingPrices}
                          isInvoiced={currentProduct?.isInvoiced}
                          readOnly={isReadOnly}
                          onRemove={canRemove ? handleRemoveProductPrice : undefined}
                        />
                      );
                    })}
                </TableBody>
                {!isLoading && !editing && !isListEmpty && (
                  <tfoot>
                    <tr className="border-double border-t-2 border-t-border">
                      <th align="left" colSpan={9} className="text-sm px-4 pt-2 pb-1">
                        <div className="flex items-center">
                          <div className="flex-1 font-medium text-white">{tCommon('total')}</div>
                          <Text
                            className="text-sm font-medium whitespace-nowrap text-white"
                            isEmpty={!isDefinedString(totalAmount)}
                          >
                            {totalAmount}
                          </Text>
                        </div>
                      </th>
                    </tr>
                  </tfoot>
                )}
              </Table>

              {/* {!editing && <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />} */}
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />

      {editing && (
        <ProductPricesAddModal
          title={title}
          open={addingProductPrices}
          selected={productPrices}
          onClose={handleCloseAddProductPrices}
          onSubmit={handleAddProductPrices}
          excludeUnitSchemes={['AreaMetric']}
          excludeProductCategories={['Felleskostnader']}
        />
      )}
    </>
  );
}
